import React, { useState, useEffect, useRef } from "react";
import "bulma/css/bulma.min.css";
import "../../../styles/ingresos.css";
import { useEmpresaSelector } from "../../../context/EmpresaSelectorContext";
import { apiRequest } from "../../../utils/api";
import * as XLSX from 'xlsx';

export default function Ingresos() {
  // Obtener variables del contexto
  const { empresaSeleccionada, esConexAdmin, empresas } = useEmpresaSelector();

  const [cuentasPorCobrar, setCuentasPorCobrar] = useState([]);
  const [loadingCuentas, setLoadingCuentas] = useState(true);
  const [errorCuentas, setErrorCuentas] = useState("");
  const [pagosRealizados, setPagosRealizados] = useState({}); // Para trackear pagos realizados
  const [cierresPorPagar, setCierresPorPagar] = useState([]); // Para mostrar cierres de caja tipo 'porPagar'
  const [clientesPorPagar, setClientesPorPagar] = useState([]); // Para cargar clientes de cierres tipo 'porPagar'


  // Estado para formulario de cuentas por cobrar
  const [nuevaCuenta, setNuevaCuenta] = useState({
    numeroFactura: "",
    cliente: "",
    contacto: "",
    descripcion: "",
    saldoCancelado: "",
    fechaFactura: new Date().toISOString().split('T')[0],
    numeroPagos: 1,
    tipoPagos: 'iguales', // 'iguales' o 'personalizados'
    pagos: [] // Para almacenar los montos personalizados
  });

  // Estado para controlar la edición de cuentas
  const [editCuentaId, setEditCuentaId] = useState(null);

  // Estado para formulario de pagos de cuentas por cobrar
  const [pagoCuenta, setPagoCuenta] = useState({
    cliente: "",
    numeroPago: "",
    monto: "",
    fechaPago: new Date().toISOString().split('T')[0],
    descripcion: "",
    tipoPago: "efectivo", // "efectivo" o "transferencia"
    bancoSeleccionado: ""
  });

  // Estado para lista de pagos realizados
  const [pagosRealizadosList, setPagosRealizadosList] = useState([]);

  // Modal para crear en todas las empresas
  const [showAllEmpresasModal, setShowAllEmpresasModal] = useState(false);
  const pendingCuentaRef = useRef(null);


  // Helper: calcular arreglo de pagos iguales (permitir decimales, no negativos)
  const calcularPagosIguales = (total, n) => {
    const nParsed = Number.isFinite(Number(n)) ? Number(n) : parseInt(n || '1');
    const cuotas = Math.max(1, Math.min(6, nParsed || 1));
    const monto = Math.max(0, Number(total || 0));
    const base = Math.floor((monto / cuotas) * 100) / 100; // base redondeada a 2 decimales hacia abajo
    let restoCents = Math.round(monto * 100 - base * 100 * cuotas); // centavos restantes
    const arr = Array.from({ length: cuotas }, () => base);
    for (let i = 0; i < cuotas && restoCents > 0; i++) {
      arr[i] = Number((arr[i] + 0.01).toFixed(3));
      restoCents -= 1;
    }
    return arr.map(v => v.toFixed(3));
  };

  // Actualizar pagos cuando cambia el número de pagos o el monto total
  const actualizarPagos = (total, n, tipo) => {
    if (tipo === 'iguales') {
      return calcularPagosIguales(total, n);
    } else {
      // Mantener los valores personalizados existentes o inicializar con 0
      const pagosActuales = [...nuevaCuenta.pagos];
      while (pagosActuales.length < n) {
        pagosActuales.push('0.00');
      }
      return pagosActuales.slice(0, n);
    }
  };

  // Editar cuenta por cobrar (ajustado a la lógica de CxP)
  const handleEditCuenta = (cuenta) => {
    setEditCuentaId(cuenta.id);
    const saldoCancelado = parseFloat(cuenta.saldoCancelado || cuenta.saldoAdeudado || 0);
    const pagos = [cuenta.pago1, cuenta.pago2, cuenta.pago3, cuenta.pago4, cuenta.pago5, cuenta.pago6]
      .filter(p => p != null && p !== '' && Number(p) > 0);
    const n = cuenta.numeroPagos || pagos.length || 1;

    // Verificar si los pagos son iguales o personalizados
    const esIgual = pagos.length > 1 && new Set(pagos).size === 1;

    setNuevaCuenta({
      numeroFactura: cuenta.numeroFactura || "",
      cliente: cuenta.cliente || "",
      contacto: cuenta.contacto || "",
      descripcion: cuenta.descripcion || "",
      saldoCancelado: isFinite(saldoCancelado) ? String(saldoCancelado) : "",
      fechaFactura: cuenta.fechaFactura ? cuenta.fechaFactura.split('T')[0] : new Date().toISOString().split('T')[0],
      numeroPagos: n,
      tipoPagos: esIgual ? 'iguales' : 'personalizados',
      pagos: esIgual ? [] : pagos.map(p => String(p))
    });
  };

  // Actualizar cuenta por cobrar
  const handleUpdateCuenta = async (e) => {
    e.preventDefault();
    if (!nuevaCuenta.cliente || !nuevaCuenta.saldoCancelado) {
      setErrorCuentas("Los campos Cliente y Saldo Cancelado son obligatorios");
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const nParsed = Number.isFinite(Number(nuevaCuenta.numeroPagos)) ? Number(nuevaCuenta.numeroPagos) : parseInt(nuevaCuenta.numeroPagos || '1');
      const cuotas = Math.max(1, Math.min(6, nParsed || 1));

      // Calcular los montos de los pagos
      let montosPagos = [];
      if (nuevaCuenta.tipoPagos === 'iguales') {
        montosPagos = calcularPagosIguales(nuevaCuenta.saldoCancelado, cuotas);
      } else {
        // Tomar los montos personalizados
        montosPagos = [...nuevaCuenta.pagos];
        // Rellenar con ceros si faltan
        while (montosPagos.length < cuotas) {
          montosPagos.push('0.00');
        }
        // Asegurar que solo se tomen los necesarios
        montosPagos = montosPagos.slice(0, cuotas);
      }

      // Mapear a payload esperado por backend
      const cuentaData = {
        cliente: nuevaCuenta.cliente,
        saldoCancelado: parseFloat(nuevaCuenta.saldoCancelado),
        fechaFactura: new Date(nuevaCuenta.fechaFactura + 'T12:00:00').toISOString(),
        letraChica: 'CONVENIO',
        empresaId: empresaSeleccionada?.id === 'all' ? null : empresaSeleccionada?.id || null,
        // Campos adicionales opcionales
        numeroFactura: nuevaCuenta.numeroFactura || undefined,
        contacto: nuevaCuenta.contacto || undefined,
        descripcion: nuevaCuenta.descripcion || undefined,
        numeroPagos: cuotas,
        // Asignar los pagos calculados - siempre asignar todos los pagos
        pago1: montosPagos[0] || null,
        pago2: montosPagos[1] || null,
        pago3: montosPagos[2] || null,
        pago4: montosPagos[3] || null,
        pago5: montosPagos[4] || null,
        pago6: montosPagos[5] || null,
      };
      const response = await apiRequest(`/cuentas-por-cobrar/${editCuentaId}`, {
        method: 'PUT',
        body: JSON.stringify(cuentaData),
      });

      if (!response.ok) {
        throw new Error('Error al actualizar cuenta por cobrar');
      }

      await cargarCuentasPorCobrar();
      setEditCuentaId(null);
      setNuevaCuenta({
        numeroFactura: "",
        cliente: "",
        contacto: "",
        descripcion: "",
        saldoCancelado: "",
        fechaFactura: new Date().toISOString().split('T')[0],
        numeroPagos: 1,
        tipoPagos: 'iguales',
        pagos: []
      });
      setErrorCuentas("");
    } catch (error) {
      console.error('Error actualizando cuenta por cobrar:', error);
      setErrorCuentas('Error al actualizar la cuenta por cobrar');
    }
  };

  // Función para cargar las cuentas por cobrar
  const cargarCuentasPorCobrar = async () => {
    try {
      setLoadingCuentas(true);

      // Construir la URL con el filtro de empresa si es necesario
      let url = '/cuentas-por-cobrar';
      if (empresaSeleccionada && empresaSeleccionada.id !== 'all') {
        url += `?empresaId=${empresaSeleccionada.id}`;
      }

      const response = await apiRequest(url);

      if (!response.ok) {
        throw new Error('Error al cargar las cuentas por cobrar');
      }

      const data = await response.json();
      setCuentasPorCobrar(data);
      setErrorCuentas('');
    } catch (error) {
      console.error('Error al cargar cuentas por cobrar:', error);
      setErrorCuentas('No se pudieron cargar las cuentas por cobrar');
    } finally {
      setLoadingCuentas(false);
    }
  };

  // Cargar datos al montar el componente y cuando cambie la empresa
  useEffect(() => {
    if (empresaSeleccionada) {
      cargarCuentasPorCobrar();
      cargarPagosRealizados();
    }
  }, [empresaSeleccionada?.id]);

  // Cargar pagos realizados desde cierres de caja
  const cargarPagosRealizados = async () => {
    try {
      let endpoint = '/cierres-caja';
      if (empresaSeleccionada && empresaSeleccionada.id !== 'all') {
        endpoint += `?empresaId=${empresaSeleccionada.id}`;
      }
      const response = await apiRequest(endpoint);
      if (response.ok) {
        const cierres = await response.json();
        const pagosTrack = {};
        const cierresPorPagarList = [];
        const pagosRealizadosListTemp = [];

        cierres.forEach(cierre => {
          if (cierre.tipoPago === 'porPagar') {
            cierresPorPagarList.push(cierre);
            if (cierre.cliente && cierre.numeroPago) {
              // Para pagos por cobrar, trackeamos por cliente y número de pago específico
              const key = `${cierre.cliente}-pago${cierre.numeroPago}`; // cliente-pago1, cliente-pago2, etc.
              pagosTrack[key] = true;
            }
          }
          // Agregar todos los cierres que son pagos realizados (no 'porPagar')
          if (cierre.tipoPago !== 'porPagar') {
            pagosRealizadosListTemp.push(cierre);
          }
        });

        // Extraer clientes únicos de cierres por pagar
        const clientesUnicos = [...new Set(cierresPorPagarList.map(c => c.cliente).filter(c => c))];
        setPagosRealizados(pagosTrack);
        setCierresPorPagar(cierresPorPagarList);
        setClientesPorPagar(clientesUnicos);
        setPagosRealizadosList(pagosRealizadosListTemp);
      } else {
        console.error('Error en respuesta de cierres:', response.status);
      }
    } catch (error) {
      console.error('Error cargando pagos realizados:', error);
    }
  };


  // ========== FUNCIONES PARA PAGOS DE CUENTAS POR COBRAR ==========

  // Cargar datos de cuenta existente para pago
  const cargarCuentaParaPago = (cuenta) => {
    // Determinar el siguiente número de pago disponible
    const pagosCliente = pagosRealizadosList.filter(p => p.cliente === cuenta.cliente);
    const numeroPagosCuenta = cuenta.numeroPagos || 1;
    const ultimoPago = Math.max(...pagosCliente.map(p => p.numeroPago || 0), 0);
    const siguientePago = ultimoPago + 1;

    // Solo permitir crear pago si no se ha completado todos los pagos
    if (siguientePago > numeroPagosCuenta) {
      alert(`La cuenta de ${cuenta.cliente} ya tiene todos sus pagos completados.`);
      return;
    }

    // Obtener el monto del pago correspondiente
    let montoPago = cuenta[`pago${siguientePago}`];

    // Si no hay monto específico para este pago, calcularlo
    if (!montoPago) {
      // Intentar calcular el monto basado en el total y el número de pagos
      const total = parseFloat(cuenta.saldoCancelado || 0);
      montoPago = (total / numeroPagosCuenta).toFixed(2);
    }

    setPagoCuenta(prev => ({
      ...prev,
      cliente: cuenta.cliente,
      numeroPago: siguientePago.toString(),
      monto: parseFloat(montoPago).toFixed(2),
      fechaPago: new Date().toISOString().split('T')[0],
      descripcion: `Pago ${siguientePago} de ${numeroPagosCuenta} - ${cuenta.numeroFactura || 'N/A'}`,
      tipoPago: "efectivo", // Reset to default
      bancoSeleccionado: ""
    }));
  };

  // Crear pago de cuenta por cobrar
  const handleCrearPago = async (e) => {
    e.preventDefault();
    if (!pagoCuenta.cliente || !pagoCuenta.numeroPago || !pagoCuenta.monto || !pagoCuenta.fechaPago) {
      alert("Todos los campos son obligatorios");
      return;
    }

    try {
      const pagoData = {
        cliente: pagoCuenta.cliente,
        numeroPago: parseInt(pagoCuenta.numeroPago),
        pagado: parseFloat(pagoCuenta.monto),
        fecha: pagoCuenta.fechaPago,
        descripcion: pagoCuenta.descripcion || `Pago ${pagoCuenta.numeroPago}`,
        tipoPago: pagoCuenta.tipoPago,
        bancoSeleccionado: pagoCuenta.tipoPago === "transferencia" ? pagoCuenta.bancoSeleccionado : undefined,
        empresaId: empresaSeleccionada?.id === 'all' ? null : empresaSeleccionada?.id || null
      };

      const response = await apiRequest('/cierres-caja', {
        method: 'POST',
        body: JSON.stringify(pagoData),
      });

      if (!response.ok) {
        throw new Error('Error al crear pago');
      }

      // Recargar datos inmediatamente para mostrar el nuevo pago
      await cargarPagosRealizados();

      // Limpiar formulario
      setPagoCuenta({
        cliente: "",
        numeroPago: "",
        monto: "",
        fechaPago: new Date().toISOString().split('T')[0],
        descripcion: "",
        tipoPago: "efectivo",
        bancoSeleccionado: ""
      });

      alert("Pago creado exitosamente");
    } catch (error) {
      console.error('Error creando pago:', error);
      alert('Error al crear el pago');
    }
  };


  // ========== FUNCIONES PARA CUENTAS POR COBRAR ==========

  // Crear nueva cuenta por cobrar
  const handleAddCuenta = async (e) => {
    e.preventDefault();
    if (!nuevaCuenta.cliente || !nuevaCuenta.saldoCancelado || isNaN(parseFloat(nuevaCuenta.saldoCancelado)) || parseFloat(nuevaCuenta.saldoCancelado) <= 0) {
      setErrorCuentas("Los campos Cliente y Saldo Cancelado son obligatorios y deben ser un número positivo");
      return;
    }

    if (esConexAdmin && (!empresaSeleccionada || empresaSeleccionada.id === 'all')) {
      // Almacenar los datos pendientes y mostrar modal de confirmación
      pendingCuentaRef.current = { type: 'cuenta', event: e };
      setShowAllEmpresasModal(true);
      return;
    }

    // Validar montos personalizados si es el caso
    if (nuevaCuenta.tipoPagos === 'personalizados') {
      const totalPagos = nuevaCuenta.pagos.reduce((sum, p) => sum + parseFloat(p || 0), 0);
      const totalEsperado = parseFloat(nuevaCuenta.saldoCancelado);

      if (Math.abs(totalPagos - totalEsperado) > 0.01) {
        setErrorCuentas(`La suma de los pagos (${totalPagos.toFixed(2)}) no coincide con el saldo cancelado (${totalEsperado.toFixed(2)})`);
        return;
      }
    }

    try {
      const token = localStorage.getItem('token');
      const nParsed = Number.isFinite(Number(nuevaCuenta.numeroPagos)) ? Number(nuevaCuenta.numeroPagos) : parseInt(nuevaCuenta.numeroPagos || '1');
      const cuotas = Math.max(1, Math.min(6, nParsed || 1));

      // Calcular los montos de los pagos
      let montosPagos = [];
      if (nuevaCuenta.tipoPagos === 'iguales') {
        montosPagos = calcularPagosIguales(nuevaCuenta.saldoCancelado, cuotas);
      } else {
        // Tomar los montos personalizados
        montosPagos = [...nuevaCuenta.pagos];
        // Rellenar con ceros si faltan
        while (montosPagos.length < cuotas) {
          montosPagos.push('0.00');
        }
        // Asegurar que solo se tomen los necesarios
        montosPagos = montosPagos.slice(0, cuotas);
      }

      // Mapear a payload esperado por backend
      const cuentaData = {
        cliente: nuevaCuenta.cliente,
        saldoCancelado: parseFloat(nuevaCuenta.saldoCancelado),
        fechaFactura: new Date(nuevaCuenta.fechaFactura + 'T12:00:00').toISOString(),
        letraChica: 'CONVENIO',
        empresaId: empresaSeleccionada?.id === 'all' ? null : empresaSeleccionada?.id || null,
        // Campos adicionales opcionales
        numeroFactura: nuevaCuenta.numeroFactura || undefined,
        contacto: nuevaCuenta.contacto || undefined,
        descripcion: nuevaCuenta.descripcion || undefined,
        numeroPagos: cuotas,
        // Asignar los pagos calculados - siempre asignar todos los pagos
        pago1: montosPagos[0] || null,
        pago2: montosPagos[1] || null,
        pago3: montosPagos[2] || null,
        pago4: montosPagos[3] || null,
        pago5: montosPagos[4] || null,
        pago6: montosPagos[5] || null,
      };
      const response = await apiRequest('/cuentas-por-cobrar', {
        method: 'POST',
        body: JSON.stringify(cuentaData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error al crear cuenta por cobrar:', errorData);
        throw new Error('Error al crear cuenta por cobrar');
      }

      const createdCuenta = await response.json();
      await cargarCuentasPorCobrar();
      setNuevaCuenta({
        numeroFactura: "",
        cliente: "",
        contacto: "",
        descripcion: "",
        saldoCancelado: "",
        fechaFactura: new Date().toISOString().split('T')[0],
        numeroPagos: 1,
        tipoPagos: 'iguales',
        pagos: []
      });
      setErrorCuentas("");

      // Recargar pagos realizados después de crear
      await cargarPagosRealizados();
      await cargarCuentasPorCobrar();
    } catch (error) {
      console.error('Error creando cuenta por cobrar:', error);
      setErrorCuentas('Error al crear la cuenta por cobrar');
    }
  };

  // Crear cuenta para todas las empresas (cuando se confirma en el modal)
  const handleCrearParaTodasEmpresas = async () => {
    if (!pendingCuentaRef.current) {
      setShowAllEmpresasModal(false);
      return;
    }

    if (!nuevaCuenta.cliente || !nuevaCuenta.saldoCancelado || isNaN(parseFloat(nuevaCuenta.saldoCancelado)) || parseFloat(nuevaCuenta.saldoCancelado) <= 0) {
      setErrorCuentas("Los campos Cliente y Saldo Cancelado son obligatorios y deben ser un número positivo");
      setShowAllEmpresasModal(false);
      return;
    }

    try {
      const nParsed = Number.isFinite(Number(nuevaCuenta.numeroPagos)) ? Number(nuevaCuenta.numeroPagos) : parseInt(nuevaCuenta.numeroPagos || '1');
      const cuotas = Math.max(1, Math.min(6, nParsed || 1));

      let montosPagos = [];
      if (nuevaCuenta.tipoPagos === 'iguales') {
        montosPagos = calcularPagosIguales(nuevaCuenta.saldoCancelado, cuotas);
      } else {
        montosPagos = [...nuevaCuenta.pagos];
        while (montosPagos.length < cuotas) montosPagos.push('0.00');
        montosPagos = montosPagos.slice(0, cuotas);
      }

      for (const empresa of empresas) {
        const cuentaData = {
          cliente: nuevaCuenta.cliente,
          saldoCancelado: parseFloat(nuevaCuenta.saldoCancelado),
          fechaFactura: new Date(nuevaCuenta.fechaFactura + 'T12:00:00').toISOString(),
          letraChica: 'CONVENIO',
          empresaId: empresa.id,
          numeroFactura: nuevaCuenta.numeroFactura || undefined,
          contacto: nuevaCuenta.contacto || undefined,
          descripcion: nuevaCuenta.descripcion || undefined,
          numeroPagos: cuotas,
          pago1: montosPagos[0] || null,
          pago2: montosPagos[1] || null,
          pago3: montosPagos[2] || null,
          pago4: montosPagos[3] || null,
          pago5: montosPagos[4] || null,
          pago6: montosPagos[5] || null,
        };

        const response = await apiRequest('/cuentas-por-cobrar', {
          method: 'POST',
          body: JSON.stringify(cuentaData),
        });

        if (response.ok) {
        } else {
          console.error('Error creando cuenta para empresa', empresa.nombre);
        }
      }

      await cargarCuentasPorCobrar();
      setNuevaCuenta({
        numeroFactura: "",
        cliente: "",
        contacto: "",
        descripcion: "",
        saldoCancelado: "",
        fechaFactura: new Date().toISOString().split('T')[0],
        numeroPagos: 1,
        tipoPagos: 'iguales',
        pagos: []
      });
      setErrorCuentas("");
      await cargarPagosRealizados();
      await cargarCuentasPorCobrar();
    } catch (error) {
      console.error('Error creando cuentas para todas las empresas:', error);
      setErrorCuentas('Error al crear las cuentas para todas las empresas');
    } finally {
      setShowAllEmpresasModal(false);
      pendingCuentaRef.current = null;
    }
  };

  // Cancelar edición de cuenta por cobrar
  const handleCancelEditCuenta = () => {
    setEditCuentaId(null);
    setNuevaCuenta({
      numeroFactura: "",
      cliente: "",
      contacto: "",
      descripcion: "",
      saldoCancelado: "",
      fechaFactura: new Date().toISOString().split('T')[0],
      numeroPagos: 1,
      tipoPagos: 'iguales',
      pagos: []
    });
    setErrorCuentas("");
  };

  // Manejar cambio en el tipo de pagos (iguales o personalizados)
  const handleTipoPagosChange = (tipo) => {
    const total = parseFloat(nuevaCuenta.saldoCancelado) || 0;
    const n = nuevaCuenta.numeroPagos || 1;

    setNuevaCuenta(prev => {
      // Si cambia a personalizados, inicializar con montos iguales
      const nuevosPagos = tipo === 'personalizados'
        ? Array(n).fill('').map((_, i) =>
          (total / n).toFixed(2)
        )
        : prev.pagos;

      return {
        ...prev,
        tipoPagos: tipo,
        pagos: nuevosPagos
      };
    });
  };

  // Manejar cambio en un pago personalizado
  const handlePagoChange = (index, value) => {
    const newPagos = [...nuevaCuenta.pagos];
    newPagos[index] = value || '0.00';
    setNuevaCuenta(prev => ({
      ...prev,
      pagos: newPagos
    }));
  };

  // Manejar eliminación de cuenta por cobrar
  const handleDeleteCuenta = async (id) => {
    if (!window.confirm('¿Está seguro de que desea eliminar esta cuenta por cobrar?')) {
      return;
    }

    try {
      const response = await apiRequest(`/cuentas-por-cobrar/${id}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Error al eliminar la cuenta por cobrar');
      }

      // Actualizar la lista de cuentas
      await cargarCuentasPorCobrar();

      // Mostrar mensaje de éxito
      setErrorCuentas('');
      alert('Cuenta por cobrar eliminada correctamente');
    } catch (error) {
      console.error('Error eliminando cuenta por cobrar:', error);
      setErrorCuentas('Error al eliminar la cuenta por cobrar');
    }
  };

  // Función para eliminar pago de la tabla de pagos realizados
  const handleEliminarPago = async (pago) => {
    if (!window.confirm('¿Está seguro de que desea eliminar este pago?')) {
      return;
    }
    if (!pago || !pago.id) {
      alert('Error: No se puede identificar el pago a eliminar');
      return;
    }

    try {
      const response = await apiRequest(`/cierres-caja/${pago.id}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error del servidor:', errorData);
        throw new Error('Error al eliminar el pago');
      }

      // Recargar datos para actualizar las tablas
      await cargarPagosRealizados();
      await cargarCuentasPorCobrar();

      alert('Pago eliminado correctamente');
    } catch (error) {
      console.error('Error eliminando pago:', error);
      alert('Error al eliminar el pago: ' + error.message);
    }
  };


  // Calcular cuántas columnas de Pago mostrar en la tabla según los datos cargados
  const maxPagosListado = Math.max(
    1,
    ...cuentasPorCobrar.map(c => {
      const pagosArr = [c.pago1, c.pago2, c.pago3, c.pago4, c.pago5, c.pago6];
      const count = pagosArr.filter(p => p != null && p !== '' && Number(p) > 0).length;
      return c.numeroPagos || count || 1;
    })
  );

  // Función para comparar fechas ignorando la hora y zona horaria
  const esMismoDia = (fecha1, fecha2) => {
    if (!fecha1 || !fecha2) return false;

    // Convertir ambas fechas a cadenas de fecha en formato YYYY-MM-DD para comparación
    const toLocalDateString = (dateStr) => {
      const date = new Date(dateStr);
      // Ajustar la fecha sumando la diferencia de la zona horaria
      const offset = date.getTimezoneOffset();
      const adjustedDate = new Date(date.getTime() + (offset * 60 * 1000));
      return adjustedDate.toISOString().split('T')[0];
    };

    const fecha1Str = toLocalDateString(fecha1);
    const fecha2Str = toLocalDateString(fecha2);
    return fecha1Str === fecha2Str;
  };

  // Estado para filtro de fecha
  const [fechaFiltroExcel, setFechaFiltroExcel] = useState('');

  // Filtrar cuentas por cobrar según fecha seleccionada
  const cuentasFiltradas = fechaFiltroExcel
    ? cuentasPorCobrar.filter((cuenta) => cuenta.fechaFactura && esMismoDia(cuenta.fechaFactura, fechaFiltroExcel))
    : cuentasPorCobrar;

  // Descargar Excel con filtro de fecha factura
  const descargarExcel = () => {
    if (cuentasFiltradas.length === 0) {
      alert('No hay cuentas por cobrar para exportar con el filtro seleccionado');
      return;
    }

    const worksheetData = cuentasFiltradas.map((cuenta) => ({
      'N° Factura': cuenta.numeroFactura || '',
      'Cliente': cuenta.cliente || '',
      'CI/RUC': cuenta.ciRuc || '',
      'Contacto': cuenta.contacto || '',
      'Descripción': cuenta.descripcion || '',
      '# de Pagos': cuenta.numeroPagos || 1,
      'Saldo Cancelado': parseFloat(cuenta.saldoCancelado || cuenta.saldoAdeudado || 0).toFixed(2),
      'Fecha Factura': cuenta.fechaFactura ? new Date(cuenta.fechaFactura).toLocaleDateString('es-ES') : '',
      'Fecha Creación': cuenta.createdAt ? new Date(cuenta.createdAt).toLocaleDateString('es-ES') : '',
      'Empresa': cuenta.empresaId || '',
      'Pago 1': cuenta.pago1 ? parseFloat(cuenta.pago1).toFixed(2) : '',
      'Pago 2': cuenta.pago2 ? parseFloat(cuenta.pago2).toFixed(2) : '',
      'Pago 3': cuenta.pago3 ? parseFloat(cuenta.pago3).toFixed(2) : '',
      'Pago 4': cuenta.pago4 ? parseFloat(cuenta.pago4).toFixed(2) : '',
      'Pago 5': cuenta.pago5 ? parseFloat(cuenta.pago5).toFixed(2) : '',
      'Pago 6': cuenta.pago6 ? parseFloat(cuenta.pago6).toFixed(2) : '',
    }));

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Cuentas por Cobrar');
    const fileName = fechaFiltroExcel ? `cuentas_por_cobrar_${fechaFiltroExcel}.xlsx` : `cuentas_por_cobrar_todas.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  return (
    <div className="ingresos-container">
      {/* Navegación de pestañas */}
      <div className="tabs is-centered is-boxed mb-5">
        <ul>
          <li className="is-active">
            <a>
              <span className="icon is-small"><i className="fas fa-dollar-sign" aria-hidden="true"></i></span>
              <span>Cuentas por Cobrar</span>
            </a>
          </li>
          <li>
            <a href="/dashboard/contabilidad/egresos">
              <span className="icon is-small"><i className="fas fa-minus" aria-hidden="true"></i></span>
              <span>Cuentas por Pagar</span>
            </a>
          </li>
          <li>
            <a href="/dashboard/contabilidad/patrimonio">
              <span className="icon is-small"><i className="fas fa-chart-pie" aria-hidden="true"></i></span>
              <span>Patrimonio</span>
            </a>
          </li>
          <li>
            <a href="/dashboard/contabilidad/calculo-costos">
              <span className="icon is-small"><i className="fas fa-calculator" aria-hidden="true"></i></span>
              <span>Cálculo de Costos</span>
            </a>
          </li>
          <li>
            <a href="/dashboard/contabilidad/resumen">
              <span className="icon is-small"><i className="fas fa-chart-bar" aria-hidden="true"></i></span>
              <span>Resumen</span>
            </a>
          </li>
        </ul>
      </div>

      {/* Mostrar información de empresa solo para conexadmin */}
      {esConexAdmin && (
        <div className="notification is-info is-light mb-4">
          <div className="content">
            <p className="has-text-weight-semibold">
              <i className="fas fa-info-circle mr-2"></i>
              Información de Empresa
            </p>
            <p>
              Estás viendo los datos como <strong>ConexAdmin</strong>.
              Las cuentas por cobrar mostradas incluyen información de todas las empresas.
            </p>
            <p className="is-size-7">
              Puedes cambiar de empresa usando el selector en la parte superior
            </p>
          </div>
        </div>
      )}

      <h1 className="ingresos-title">Gestión de Cuentas por Cobrar</h1>

      {/* Indicador de empresa */}
      {empresaSeleccionada && (
        <div className="notification is-info is-light has-text-centered mb-4">
          <p className="has-text-weight-semibold">
            📊 Visualizando datos de: <strong>{empresaSeleccionada.nombre || 'Empresa'}</strong>
          </p>
          {esConexAdmin && (
            <p className="is-size-7">
              Puedes cambiar de empresa usando el selector en la parte superior
            </p>
          )}
        </div>
      )}


      {/* ========== SECCIÓN DE CUENTAS POR COBRAR ========== */}

      {/* Formulario para crear/editar cuenta por cobrar */}
      <div className="box mt-6 has-background-white">
        <h2 className="subtitle has-text-centered has-text-darkblue">
          {editCuentaId !== null ? "Editar Cuenta por Cobrar" : "Agregar Cuenta por Cobrar"}
        </h2>
        {errorCuentas && <p className="notification is-danger has-text-centered">{errorCuentas}</p>}
        <form onSubmit={editCuentaId !== null ? handleUpdateCuenta : handleAddCuenta} className="mb-4">
          <div className="columns is-multiline">
            <div className="column is-2">
              <label className="label is-small">N° Factura</label>
              <input
                className="input"
                type="text"
                placeholder="Número"
                value={nuevaCuenta.numeroFactura}
                onChange={(e) => setNuevaCuenta({ ...nuevaCuenta, numeroFactura: e.target.value })}
              />
            </div>
            <div className="column is-3">
              <label className="label is-small">Cliente</label>
              <div className="select is-fullwidth">
                <select
                  value={nuevaCuenta.cliente}
                  onChange={(e) => {
                    const clienteSeleccionado = e.target.value;
                    // Buscar si el cliente ya tiene una cuenta por cobrar existente (tomar la más reciente)
                    const cuentaExistente = cuentasPorCobrar
                      .filter(c => c.cliente === clienteSeleccionado)
                      .sort((a, b) => new Date(b.fechaFactura || b.fecha || 0) - new Date(a.fechaFactura || a.fecha || 0))[0];
                    if (cuentaExistente) {
                      // Si existe, cargar los datos de la cuenta existente más reciente
                      const saldoCancelado = parseFloat(cuentaExistente.saldoCancelado || cuentaExistente.saldoAdeudado || 0);

                      setNuevaCuenta({
                        ...nuevaCuenta,
                        cliente: clienteSeleccionado,
                        numeroFactura: cuentaExistente.numeroFactura || '',
                        contacto: cuentaExistente.contacto || '',
                        descripcion: cuentaExistente.descripcion || '',
                        saldoCancelado: saldoCancelado.toString(),
                        fechaFactura: cuentaExistente.fechaFactura ? cuentaExistente.fechaFactura.split('T')[0] : new Date().toISOString().split('T')[0],
                        numeroPagos: cuentaExistente.numeroPagos || 1
                      });
                    } else {
                      // Si no existe, buscar datos del cierre de caja más reciente para este cliente
                      const cierreReciente = cierresPorPagar
                        .filter(c => c.cliente === clienteSeleccionado)
                        .sort((a, b) => new Date(b.fecha) - new Date(a.fecha))[0]; // Más reciente primero

                      if (cierreReciente) {
                        // Calcular el adeudado correctamente: costo * cantidad * (1 + IVA/100) - abono
                        const costo = parseFloat(cierreReciente.costo || 0);
                        const cantidad = parseFloat(cierreReciente.cantidad || 1);
                        const iva = parseFloat(cierreReciente.iva || 0) / 100;
                        const abono = parseFloat(cierreReciente.abono || 0);
                        const adeudado = (costo * cantidad * (1 + iva)) - abono;

                        // Tomar el número de factura del comprobante del cierre
                        const numeroFacturaGenerado = cierreReciente.comprobante || `FAC-${cierreReciente.id || 'AUTO'}`;

                        setNuevaCuenta({
                          ...nuevaCuenta,
                          cliente: clienteSeleccionado,
                          numeroFactura: numeroFacturaGenerado,
                          contacto: cierreReciente.contacto || cierreReciente.telefono || cierreReciente.email || '',
                          descripcion: cierreReciente.producto || 'Pago por producto',
                          saldoCancelado: adeudado.toFixed(2),
                          fechaFactura: cierreReciente.fecha ? new Date(cierreReciente.fecha).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
                          numeroPagos: 1
                        });
                      } else {
                        // Si no hay datos previos, solo cambiar el cliente
                        setNuevaCuenta({ ...nuevaCuenta, cliente: clienteSeleccionado });
                      }
                    }
                  }}
                  required
                >
                  <option value="">Seleccionar cliente</option>
                  {clientesPorPagar.map((cliente, index) => {
                    // Verificar si el cliente ya tiene una cuenta por cobrar
                    const tieneCuenta = cuentasPorCobrar.some(c => c.cliente === cliente);
                    return (
                      <option key={index} value={cliente}>
                        {cliente} {tieneCuenta ? '(Cuenta existente)' : '(Nuevo)'}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
            <div className="column is-2">
              <label className="label is-small">Contacto</label>
              <input
                className="input"
                type="text"
                placeholder="Teléfono / Email"
                value={nuevaCuenta.contacto}
                onChange={(e) => setNuevaCuenta({ ...nuevaCuenta, contacto: e.target.value })}
              />
            </div>
            <div className="column is-3">
              <label className="label is-small">Descripción</label>
              <input
                className="input"
                type="text"
                placeholder="Detalle"
                value={nuevaCuenta.descripcion}
                onChange={(e) => setNuevaCuenta({ ...nuevaCuenta, descripcion: e.target.value })}
              />
            </div>
            <div className="column is-2">
              <label className="label is-small">Saldo Cancelado</label>
              <input
                className="input"
                type="number"
                step="0.01"
                placeholder="Monto"
                value={nuevaCuenta.saldoCancelado}
                onChange={(e) => setNuevaCuenta({ ...nuevaCuenta, saldoCancelado: e.target.value })}
                required
              />
            </div>
            <div className="column is-2">
              <label className="label is-small">Fecha Factura</label>
              <input
                className="input"
                type="date"
                value={nuevaCuenta.fechaFactura}
                onChange={(e) => setNuevaCuenta({ ...nuevaCuenta, fechaFactura: e.target.value })}
                required
              />
            </div>
            <div className="column is-2">
              <label className="label is-small"># de Pagos</label>
              <div className="select is-fullwidth">
                <select
                  value={nuevaCuenta.numeroPagos}
                  onChange={(e) => {
                    const nParsed = Number.isFinite(Number(e.target.value)) ? Number(e.target.value) : parseInt(e.target.value || '1');
                    const val = Math.max(1, Math.min(6, nParsed || 1));
                    const total = parseFloat(nuevaCuenta.saldoCancelado) || 0;

                    setNuevaCuenta(prev => ({
                      ...prev,
                      numeroPagos: val,
                      pagos: prev.tipoPagos === 'personalizados'
                        ? [...prev.pagos, ...Array(Math.max(0, val - (prev.pagos?.length || 0))).fill('0.00')].slice(0, val)
                        : prev.pagos
                    }));
                  }}
                >
                  {[1, 2, 3, 4, 5, 6].map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="column is-3">
              <label className="label is-small">Tipo de Pagos</label>
              <div className="select is-fullwidth">
                <select
                  value={nuevaCuenta.tipoPagos}
                  onChange={(e) => handleTipoPagosChange(e.target.value)}
                  className="is-fullwidth"
                >
                  <option value="iguales">Pagos Iguales</option>
                  <option value="personalizados">Pagos Personalizados</option>
                </select>
              </div>
            </div>

            <div className="column is-12">
              <label className="label">
                {nuevaCuenta.tipoPagos === 'iguales' ? 'Montos de Pagos Iguales' : 'Montos de Pagos Personalizados'}
              </label>
              <div className="columns is-multiline">
                {Array.from({ length: nuevaCuenta.numeroPagos || 1 }).map((_, index) => {
                  // Calcular el monto para pagos iguales
                  const montoIgual = nuevaCuenta.saldoCancelado
                    ? (parseFloat(nuevaCuenta.saldoCancelado) / (nuevaCuenta.numeroPagos || 1)).toFixed(2)
                    : '0.00';

                  return (
                    <div key={`pago-${index}`} className="column is-2">
                      <label className="label is-small">Pago {index + 1}</label>
                      {nuevaCuenta.tipoPagos === 'iguales' ? (
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className="input"
                          value={montoIgual}
                          readOnly
                          style={{ backgroundColor: '#f5f5f5' }}
                        />
                      ) : (
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className="input"
                          value={nuevaCuenta.pagos[index] || '0.00'}
                          onChange={(e) => handlePagoChange(index, e.target.value)}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="mt-2 has-text-grey is-size-7">
                Total: ${nuevaCuenta.tipoPagos === 'iguales'
                  ? (nuevaCuenta.saldoCancelado || '0.00')
                  : nuevaCuenta.pagos.reduce((sum, p) => sum + parseFloat(p || 0), 0).toFixed(2)
                } / {nuevaCuenta.saldoCancelado || '0.00'}
              </div>
            </div>
            <div className="column is-12 has-text-centered">
              <button type="submit" className="button is-link is-medium">
                {editCuentaId !== null ? "Actualizar" : "Agregar Cuenta por Cobrar"}
              </button>
              {editCuentaId !== null && (
                <button
                  type="button"
                  className="button is-light is-medium ml-2"
                  onClick={handleCancelEditCuenta}
                >
                  Cancelar
                </button>
              )}
            </div>
          </div>
        </form>
      </div>


      {/* ========== TABLA DE CUENTAS POR COBRAR ========== */}
      <div className="box mt-6" style={{ paddingBottom: '10px' }}>
        <div className="is-flex is-justify-content-space-between is-align-items-center mb-4">
          <h2 className="title is-5">Cuentas por Cobrar</h2>
          <div className="field is-grouped">
            <div className="control">
              <input
                className="input"
                type="date"
                value={fechaFiltroExcel}
                onChange={(e) => setFechaFiltroExcel(e.target.value)}
                placeholder="Filtrar por fecha factura"
              />
            </div>
            <div className="control">
              <button
                className="button is-gold"
                onClick={descargarExcel}
                disabled={loadingCuentas || cuentasPorCobrar.length === 0}
              >
                <span className="icon">
                  <i className="fas fa-file-excel"></i>
                </span>
                <span>Descargar Excel</span>
              </button>
            </div>
          </div>
        </div>
        {loadingCuentas ? (
          <p className="has-text-centered">Cargando cuentas por cobrar...</p>
        ) : cuentasPorCobrar.length === 0 ? (
          <p className="has-text-centered">No hay cuentas por cobrar</p>
        ) : (
          <>
            <div style={{
              width: '100%',
              overflowX: 'scroll',
              border: '1px solid #dbdbdb',
              borderRadius: '4px',
              padding: '10px',
              backgroundColor: 'white',
              scrollbarWidth: 'auto',
              WebkitOverflowScrolling: 'touch',
              minHeight: '200px',
              margin: '0 auto'
            }}>
              <table className="table is-fullwidth is-striped" style={{
                minWidth: '1600px',
                width: 'max-content',
                marginBottom: '0'
              }}>
                <thead>
                  <tr>
                    <th>N° Factura</th>
                    <th>Cliente</th>
                    <th>Contacto</th>
                    <th>Descripción</th>
                    <th># de Pagos</th>
                    <th>Saldo Cancelado</th>
                    <th>Fecha Factura</th>
                    {Array.from({ length: maxPagosListado }, (_, i) => (
                      <th key={`hdr-pago-${i + 1}`}>{`Pago ${i + 1}`}</th>
                    ))}
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {cuentasPorCobrar.map((cuenta) => {
                    const saldoCancelado = parseFloat(cuenta.saldoCancelado || cuenta.saldoAdeudado || 0);
                    const pagosCount = [cuenta.pago1, cuenta.pago2, cuenta.pago3, cuenta.pago4, cuenta.pago5, cuenta.pago6].filter(p => !!p && Number(p) > 0).length;
                    const nPagos = cuenta.numeroPagos || pagosCount || 1;
                    return (
                      <tr key={cuenta.id}>
                        <td>{cuenta.numeroFactura || '-'}</td>
                        <td>{cuenta.cliente}</td>
                        <td>{cuenta.contacto || '-'}</td>
                        <td>{cuenta.descripcion || '-'}</td>
                        <td>{nPagos}</td>
                        <td>${saldoCancelado.toFixed(2)}</td>
                        <td>{cuenta.fechaFactura ? new Date(cuenta.fechaFactura).toLocaleDateString('es-ES', { timeZone: 'America/Bogota' }) : 'N/A'}</td>
                        {Array.from({ length: maxPagosListado }, (_, i) => {
                          const k = i + 1;
                          const val = cuenta[`pago${k}`];
                          const pagoKey = `${cuenta.cliente}-pago${k}`;
                          const pagosCliente = pagosRealizadosList.filter(p => p.cliente === cuenta.cliente && p.numeroPago == k);
                          const totalPagado = pagosCliente.reduce((sum, p) => sum + (parseFloat(p.pagado || p.monto || 0)), 0);

                          let icono = '';
                          let color = '';

                          if (val != null && val !== '' && Number(val) > 0) {
                            if (totalPagado >= Number(val)) {
                              icono = '✔️';
                              color = 'green';
                            } else if (totalPagado > 0) {
                              icono = '📌';
                              color = 'orange';
                            } else {
                              // Verificar si ha pasado la fecha (simplificado - usar fecha actual vs fecha de creación de la cuenta)
                              const fechaCuenta = new Date(cuenta.fechaFactura);
                              const hoy = new Date();
                              const diasTranscurridos = Math.floor((hoy - fechaCuenta) / (1000 * 60 * 60 * 24));

                              // Asumir que cada pago debe hacerse cada 30 días aproximadamente
                              const diasEsperados = (k - 1) * 30;
                              if (diasTranscurridos > diasEsperados + 30) { // 30 días de gracia
                                icono = '❌';
                                color = 'red';
                              }
                            }
                          }

                          return (
                            <td key={`pago-${cuenta.id}-${k}`}>
                              {val != null && val !== '' && Number(val) > 0 ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                  <span>${Number(val).toFixed(2)}</span>
                                  {icono && <span style={{ color, fontSize: '1.2em' }}>{icono}</span>}
                                </div>
                              ) : ''}
                            </td>
                          );
                        })}
                        <td>
                          <button
                            className="button is-small is-info mr-2"
                            onClick={() => handleEditCuenta(cuenta)}
                            title="Editar"
                          >
                            <span className="icon"><i className="fas fa-edit"></i></span>
                          </button>
                          <button
                            className="button is-small is-danger"
                            onClick={() => handleDeleteCuenta(cuenta.id)}
                            title="Eliminar"
                          >
                            <span className="icon"><i className="fas fa-trash"></i></span>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* Resumen de totales al final */}
            <div className="box mt-4" style={{ background: '#eaf3ff' }}>
              <div className="is-flex is-justify-content-space-around is-flex-wrap-wrap has-text-link">
                <p className="has-text-weight-semibold" style={{ fontSize: '1.05rem' }}>Total Cuentas: {cuentasPorCobrar.length}</p>
                <p className="has-text-weight-semibold" style={{ fontSize: '1.05rem' }}>Total a Cancelar: ${cuentasPorCobrar.reduce((sum, c) => sum + (parseFloat(c.saldoCancelado || c.saldoAdeudado || 0)), 0).toFixed(2)}</p>
              </div>
            </div>
          </>
        )}
      </div>


      {/* ========== SECCIÓN DE PAGOS DE CUENTAS POR COBRAR ========== */}

      {/* Formulario para crear pagos de cuentas por cobrar */}
      <div className="box mt-6 has-background-light">
        <h2 className="subtitle has-text-centered has-text-primary">
          Pagar Cuenta por Cobrar
        </h2>
        <form className="mb-4" onSubmit={handleCrearPago}>
          <div className="columns is-multiline">
            <div className="column is-3">
              <label className="label is-small">Cliente</label>
              <div className="select is-fullwidth">
                <select
                  value={pagoCuenta.cliente}
                  onChange={(e) => {
                    const clienteSeleccionado = e.target.value;
                    const cuentaExistente = cuentasPorCobrar.find(c => c.cliente === clienteSeleccionado);
                    if (cuentaExistente) {
                      cargarCuentaParaPago(cuentaExistente);
                    }
                  }}
                  required
                >
                  <option value="">Seleccionar cliente</option>
                  {cuentasPorCobrar.map((cuenta, index) => (
                    <option key={index} value={cuenta.cliente}>
                      {cuenta.cliente}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="column is-2">
              <label className="label is-small">Número de Pago</label>
              <input
                className="input"
                type="text"
                value={pagoCuenta.numeroPago}
                readOnly
              />
            </div>
            <div className="column is-2">
              <label className="label is-small">Monto del Pago</label>
              <input
                className="input"
                type="number"
                step="0.01"
                value={pagoCuenta.monto}
                readOnly
              />
            </div>
            <div className="column is-2">
              <label className="label is-small">Tipo de Pago</label>
              <div className="select is-fullwidth">
                <select
                  value={pagoCuenta.tipoPago}
                  onChange={(e) => setPagoCuenta({ ...pagoCuenta, tipoPago: e.target.value })}
                  required
                >
                  <option value="efectivo">Efectivo</option>
                  <option value="transferencia">Transferencia</option>
                </select>
              </div>
            </div>
            {pagoCuenta.tipoPago === "transferencia" && (
              <div className="column is-2">
                <label className="label is-small">Banco</label>
                <input
                  className="input"
                  type="text"
                  placeholder="Nombre del banco"
                  value={pagoCuenta.bancoSeleccionado}
                  onChange={(e) => setPagoCuenta({ ...pagoCuenta, bancoSeleccionado: e.target.value })}
                  required
                />
              </div>
            )}
            <div className="column is-2">
              <label className="label is-small">Fecha del Pago</label>
              <input
                className="input"
                type="date"
                value={pagoCuenta.fechaPago}
                onChange={(e) => setPagoCuenta({ ...pagoCuenta, fechaPago: e.target.value })}
                required
              />
            </div>
            <div className="column is-3">
              <label className="label is-small">Descripción</label>
              <input
                className="input"
                type="text"
                placeholder="Detalle del pago"
                value={pagoCuenta.descripcion}
                onChange={(e) => setPagoCuenta({ ...pagoCuenta, descripcion: e.target.value })}
              />
            </div>
            <div className="column is-12 has-text-centered">
              <button type="submit" className="button is-success is-medium">
                <span className="icon"><i className="fas fa-plus"></i></span>
                <span>Crear Pago</span>
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Tabla de Pagos Realizados */}
      <div className="box mt-6">
        <h2 className="title is-5">Pagos Realizados</h2>
        {(() => {
          return pagosRealizadosList.length === 0;
        })() ? (
          <p className="has-text-centered">No hay pagos realizados registrados</p>
        ) : (
          <div style={{
            width: '100%',
            overflowX: 'scroll',
            border: '1px solid #dbdbdb',
            borderRadius: '4px',
            padding: '10px',
            backgroundColor: 'white',
            scrollbarWidth: 'auto',
            WebkitOverflowScrolling: 'touch',
            minHeight: '200px',
            margin: '0 auto'
          }}>
            <table className="table is-fullwidth is-striped" style={{
              minWidth: '800px',
              width: 'max-content',
              marginBottom: '0'
            }}>
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Número de Pago</th>
                  <th>Tipo de Pago</th>
                  <th>Monto</th>
                  <th>Fecha del Pago</th>
                  <th>Descripción</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {pagosRealizadosList.map((pago, index) => (
                  <tr key={index}>
                    <td>{pago.cliente}</td>
                    <td>{pago.numeroPago}</td>
                    <td>{pago.tipoPago === 'efectivo' ? 'Efectivo' : 'Transferencia'}</td>
                    <td>${(() => {
                      const valor = parseFloat(pago.totalIngresos || pago.pagado || pago.monto || pago.abono || 0);
                      return `$${valor.toFixed(2)}`;
                    })()}</td>
                    <td>{pago.fecha ? new Date(pago.fecha).toLocaleDateString('es-ES', { timeZone: 'America/Bogota' }) : 'N/A'}</td>
                    <td>{pago.descripcion || '-'}</td>
                    <td>
                      <button
                        className="button is-small is-danger"
                        onClick={() => handleEliminarPago(pago)}
                        title="Eliminar pago"
                      >
                        <span className="icon"><i className="fas fa-trash"></i></span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>


      {/* Modal de confirmación para crear en todas las empresas */}
      {showAllEmpresasModal && (
        <div className="modal is-active">
          <div className="modal-background" onClick={() => { setShowAllEmpresasModal(false); pendingCuentaRef.current = null; }}></div>
          <div className="modal-card">
            <header className="modal-card-head" style={{ backgroundColor: '#1a1a2e' }}>
              <p className="modal-card-title has-text-warning">⚠️ Confirmar creación para todas las empresas</p>
              <button className="delete" aria-label="close" onClick={() => { setShowAllEmpresasModal(false); pendingCuentaRef.current = null; }}></button>
            </header>
            <section className="modal-card-body">
              <div className="content">
                <p className="has-text-weight-semibold is-size-5">
                  ¿Estás seguro de que deseas crear esta cuenta por cobrar para <strong>todas las empresas</strong>?
                </p>
                <p>
                  Se creará un registro en cada una de las <strong>{empresas.length}</strong> empresas registradas actualmente:
                </p>
                <ul>
                  {empresas.map(emp => (
                    <li key={emp.id}>{emp.nombre}</li>
                  ))}
                </ul>
                <div className="notification is-warning is-light">
                  <strong>Nota:</strong> Esta acción creará {empresas.length} registros, uno por cada empresa.
                </div>
              </div>
            </section>
            <footer className="modal-card-foot" style={{ justifyContent: 'flex-end' }}>
              <button className="button" onClick={() => { setShowAllEmpresasModal(false); pendingCuentaRef.current = null; }}>
                No, Cancelar
              </button>
              <button className="button is-warning has-text-dark" onClick={handleCrearParaTodasEmpresas}>
                Sí, Crear para todas
              </button>
            </footer>
          </div>
        </div>
      )}

    </div>
  );
}